import { Add, RadioButtonChecked } from '@mui/icons-material';
import { Box, Divider, Stack, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { Fragment, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useRecommendationById } from '/@/hooks/my-recommendations/useFilterMyRecommendations';
import { _ } from '/@/locale';
import { getCerealDiseaseMonths, getSeedTreatmentMonths } from '/@/store/crop-protection/phenology';
import {
    getApplicationOptions,
    getApplicationTypeOptions,
    getCropProtectionForm,
} from '/@/store/crop-protection/selectors';
import { getPhenologyIconMarginBottom, type Option, PhenologiesIcons } from '/@/types/crop-protection';

const CropProtectionRecommendationDetailCard = () => {
    const theme = useTheme();
    const form = useSelector(getCropProtectionForm);
    const { recommendationId } = useParams();
    const recoQuery = useRecommendationById(Number(recommendationId));
    const productRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const [maxHeight, setMaxHeight] = useState({});
    const titleRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const conditionRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const [maxTitleHeightByColumn, setMaxTitleHeightByColumn] = useState({});
    const [maxConditionHeightByColumn, setMaxConditionHeightByColumn] = useState({});
    const [selectedOptionIndex, setSelectedOptionIndex] = useState<number>(0);
    const renderMonth = (month: string, disabled = false) => (
        <Box
            sx={{
                background: disabled ? '#F5F5F5' : theme.palette.primary.dark,
                color: disabled ? 'gray !important' : 'white !important',
                padding: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <Typography variant='subtitle2'>{month}</Typography>
        </Box>
    );

    const renderProduct = (name: string, dose: string, logoSrc: string, index: number) => (
        <Stack
            ref={(el) => (productRefs.current[`${name}-${index}`] = el)}
            sx={{
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                background: '#E7F8FF',
                borderRadius: 2,
                py: 2,
                px: 1,
                minWidth: 120,
                height: maxHeight[selectedOptionIndex] ? `${maxHeight[selectedOptionIndex]}px` : 'auto',
                transition: 'height 0.3s ease',
            }}
        >
            {logoSrc && (
                <Box
                    component='img'
                    src={logoSrc}
                    alt={name}
                    sx={{
                        width: 50,
                        height: 50,
                    }}
                />
            )}
            <Typography variant='caption' fontWeight={'bold'}>
                {name}
            </Typography>
            <Typography variant='caption'>{dose}</Typography>
        </Stack>
    );

    const parsedOptions: Option[] = useMemo(() => {
        const options =
            recoQuery.data?.weedV2?.result?.options ||
            recoQuery.data?.diseaseRecommendation?.result?.options ||
            recoQuery.data?.plagueRecommendation?.result?.options ||
            recoQuery.data?.seedTreatmentRecommendation?.result?.options ||
            [];
        return (
            options.map((option) => ({
                name: _(option.name),
                comments: _(option.comments),
                type: option.type,
                columns: option.columns.map((column) => ({
                    name: _(column.name),
                    condition: _(column.condition),
                    products: column.products.map((product) => ({
                        dose: _(product.dose),
                        name: _(product.product.name),
                        image: product.product.image,
                    })),
                })),
            })) || []
        );
    }, [recoQuery.data]);
    const selectedOption = parsedOptions[selectedOptionIndex];

    const applicationOptions = useMemo(() => getApplicationOptions(), []);
    const recommendationApplicationMoment =
        recoQuery.data?.weedV2?.result?.applicationMoment ||
        recoQuery.data?.plagueRecommendation?.result?.applicationMoment;
    const currentApplication = applicationOptions[recommendationApplicationMoment];

    const applicationTypeOptions = useMemo(() => getApplicationTypeOptions(), []);
    const currentApplicationType =
        applicationTypeOptions[recoQuery.data?.diseaseRecommendation?.result?.applicationType];

    const months = useMemo(() => {
        if (!recoQuery.data || !selectedOption) return [];
        if (recoQuery.data?.seedTreatmentRecommendation) {
            return getSeedTreatmentMonths(selectedOption, recoQuery.data.seedTreatmentRecommendation.result?.crop);
        }
        if (!currentApplication && !currentApplicationType) return [];
        if (recoQuery.data?.weedV2 || recoQuery.data?.plagueRecommendation) {
            const reco = recoQuery.data?.weedV2 || recoQuery.data?.plagueRecommendation;
            return (
                currentApplication?.getMonths({
                    plantingDate: reco.plantingDate,
                    createdDate: recoQuery.data.created,
                    option: selectedOption, // TODO: TAB
                    applicationWindow: reco.result?.applicationWindow,
                    crop: reco.result?.crop,
                }) || []
            );
        }
        if (recoQuery.data?.diseaseRecommendation && recoQuery.data?.diseaseRecommendation.result.crop === 'soy') {
            return [
                {
                    disabled: true,
                },
                {
                    disabled: false,
                    columns: selectedOption.columns,
                    icons: ['r3', 'r4', 'r5'],
                },
                {
                    disabled: true,
                },
            ];
        }
        if (recoQuery.data?.diseaseRecommendation && recoQuery.data?.diseaseRecommendation.result.crop === 'corn') {
            return [
                {
                    disabled: true,
                },
                {
                    disabled: false,
                    columns: selectedOption.columns,
                    icons: ['v8', 'v9', 'v10', 'r1'],
                },
                {
                    disabled: true,
                },
            ];
        }
        if (['wheat', 'barley'].includes(recoQuery.data?.diseaseRecommendation?.result?.crop)) {
            return getCerealDiseaseMonths(selectedOption);
        }
        return [];
    }, [currentApplication, currentApplicationType, form, recoQuery.data, selectedOption]);
    const applicationTypeIndexes = useMemo(() => {
        let startIndex: number | undefined;
        let endIndex: number | undefined;
        months.forEach((month, index) => {
            if (!month.disabled) {
                if (startIndex === undefined) startIndex = index;
                endIndex = index;
            }
        });
        return [startIndex, endIndex];
    }, [months]);

    useLayoutEffect(() => {
        if (Object.keys(productRefs.current).length > 0) {
            const heights = Object.values(productRefs.current).map((ref) => ref?.offsetHeight || 0);
            const max = Math.max(...heights);
            setMaxHeight((prevMaxHeights) => ({
                ...prevMaxHeights,
                [selectedOptionIndex]: max,
            }));
            const conditionHeights = Object.values(conditionRefs.current).map((ref) => ref?.offsetHeight || 0);
            const conditionMax = Math.max(...conditionHeights);
            setMaxConditionHeightByColumn((prev) => ({ ...prev, [selectedOptionIndex]: conditionMax }));
            const titleHeights = Object.values(titleRefs.current).map((ref) => ref?.offsetHeight || 0);
            const titleMax = Math.max(...titleHeights);
            setMaxTitleHeightByColumn((prev) => ({ ...prev, [selectedOptionIndex]: titleMax }));
        }
    }, [months, selectedOptionIndex]);

    const generatePlagueRecommendationText = (applicationWindow?: string) => {
        if (applicationWindow === 'v2_to_v10') {
            return _('Desde estadío v2 hasta v10');
        }
        if (applicationWindow === 'v1_to_v8') {
            return _('Desde estadío v1 hasta v8');
        }
        return false;
    };

    return (
        <Stack spacing={2}>
            {parsedOptions.length > 1 && (
                <Tabs value={selectedOptionIndex} onChange={(_, value) => setSelectedOptionIndex(value)}>
                    {parsedOptions.map((option, index) => (
                        <Tab key={index} label={_('Opción') + ' ' + (index + 1)} value={index} />
                    ))}
                </Tabs>
            )}

            <Stack
                direction='row'
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 1, sm: `repeat(${months.length}, 1fr)` },
                    width: '100%',
                    overflowX: 'auto',
                }}
            >
                {months.map((month, i) => {
                    return (
                        <Stack
                            key={i}
                            sx={{
                                background: month.disabled ? '#F5F5F5' : '',
                                flex: 1,
                                borderRight: `1px dashed ${theme.palette.divider}`,
                                display: {
                                    xs:
                                        (month.columns?.length || 0) > 0 || (month.icons?.length || 0) > 0
                                            ? 'flex'
                                            : 'none',
                                    sm: 'flex',
                                },
                            }}
                        >
                            {month.name && renderMonth(month.name, month.disabled)}
                            {month.columns && (
                                <Stack direction='row' spacing={2} p={2} alignSelf={'center'}>
                                    {month.columns.map((column) => (
                                        <Stack direction='row' height={'fit-content'} key={column.name}>
                                            <Stack p={1} justifyContent='center' alignItems='center'>
                                                <RadioButtonChecked color='secondary' />
                                                <Divider
                                                    orientation='vertical'
                                                    sx={{
                                                        borderStyle: 'dashed',
                                                        borderColor: theme.palette.secondary.main,
                                                    }}
                                                />
                                            </Stack>
                                            <Stack key={column.name} flex={1} spacing={1}>
                                                <Stack>
                                                    <Typography
                                                        ref={(el) => (titleRefs.current[column.name] = el)}
                                                        variant='caption'
                                                        sx={{ height: maxTitleHeightByColumn[selectedOptionIndex] }}
                                                    >
                                                        {column.name}
                                                    </Typography>

                                                    <Typography
                                                        ref={(el) => (conditionRefs.current[column.condition] = el)}
                                                        variant='caption'
                                                        fontWeight='bold'
                                                        sx={{
                                                            height: maxConditionHeightByColumn[selectedOptionIndex],
                                                        }}
                                                    >
                                                        {column.condition}
                                                    </Typography>
                                                </Stack>

                                                <Stack spacing={1} mt={1} alignItems='center' sx={{ minHeight: 250 }}>
                                                    {column.products.map((product, index) => (
                                                        <>
                                                            {renderProduct(
                                                                product.name,
                                                                product.dose,
                                                                product.image,
                                                                index
                                                            )}
                                                            {index + 1 < column.products.length && (
                                                                <Add color='primary' />
                                                            )}
                                                        </>
                                                    ))}
                                                </Stack>
                                            </Stack>
                                        </Stack>
                                    ))}
                                </Stack>
                            )}
                            {month.icons && (
                                <Box display='flex' justifyContent='space-evenly' alignItems='center' mt={'auto'}>
                                    {month.icons.map((icon) => (
                                        <Box
                                            key={icon}
                                            sx={{
                                                maxWidth: 50,
                                                width: 50,
                                                marginBottom: getPhenologyIconMarginBottom(icon, month.disabled),
                                                '& svg': { width: '100%', height: 'auto' },
                                            }}
                                            display='flex'
                                            justifyContent='center'
                                            dangerouslySetInnerHTML={{
                                                __html: PhenologiesIcons[icon],
                                            }}
                                        />
                                    ))}
                                </Box>
                            )}
                        </Stack>
                    );
                })}
                {months?.map((month, index) => {
                    return (
                        <Box key={index}>
                            {month.icons && month.disabled && (
                                <Typography
                                    variant='caption'
                                    sx={{
                                        p: 1,
                                        background: (t) => t.palette.gray[400],
                                        color: 'grey',
                                        display: 'block',
                                        textAlign: 'center',
                                    }}
                                >
                                    {month.iconsDescriptions?.join(' - ')}
                                </Typography>
                            )}
                        </Box>
                    );
                })}
                {months?.length > 0 &&
                    // only render the application-window banner when there is an enabled
                    // month; otherwise the grid-column math below resolves to NaN
                    applicationTypeIndexes[0] !== undefined &&
                    !(
                        // do not show only for plague recos without application window
                        (
                            recoQuery.data?.plagueRecommendation &&
                            recoQuery.data?.plagueRecommendation?.result?.applicationWindow === ''
                        )
                    ) && (
                        <Typography
                            variant='caption'
                            sx={{
                                position: 'relative',
                                bottom: 36,
                                gridColumnStart: {
                                    xs: 0,
                                    sm: applicationTypeIndexes[0] + 1,
                                },
                                gridColumnEnd: {
                                    xs: 1,
                                    sm: applicationTypeIndexes[1] + 2,
                                },
                                background: '#006F9B',
                                color: 'white !important',
                                p: 1,
                                textAlign: 'center',
                            }}
                        >
                            {recoQuery.data?.weedV2
                                ? `${currentApplication?.title({
                                      crop: recoQuery.data?.weedV2?.result?.crop,
                                  })} (${currentApplication?.description({
                                      type: selectedOption?.type,
                                  })})`
                                : recoQuery.data?.plagueRecommendation
                                  ? generatePlagueRecommendationText(
                                        recoQuery.data?.plagueRecommendation.result?.applicationWindow
                                    )
                                  : recoQuery.data?.seedTreatmentRecommendation
                                    ? _('Siembra')
                                    : recoQuery.data?.diseaseRecommendation &&
                                        recoQuery.data?.diseaseRecommendation?.result?.crop === 'soy'
                                      ? _('Estadío R3 a R5')
                                      : recoQuery.data?.diseaseRecommendation?.result?.crop === 'corn'
                                        ? _('Estadío V8 a R1')
                                        : ['wheat', 'barley'].includes(
                                                recoQuery.data?.diseaseRecommendation?.result?.crop
                                            )
                                          ? _('De Encañazón a Espigazón')
                                          : ''}
                        </Typography>
                    )}
            </Stack>
            {selectedOption?.comments && (
                <Stack>
                    <Typography
                        color='primary'
                        variant='subtitle1'
                        sx={{
                            fontWeight: 700,
                        }}
                    >
                        {_('A tener en cuenta')}
                    </Typography>
                    <Typography color='primary' variant='body2'>
                        {selectedOption?.comments}
                    </Typography>
                </Stack>
            )}
        </Stack>
    );
};

export default CropProtectionRecommendationDetailCard;

export const CropProtectionRecommendationDetailCardPDF = ({ recommendation, pdfTheme }) => {
    const recommendationKey = recommendation?.weedV2
        ? 'weedV2'
        : recommendation?.diseaseRecommendation
          ? 'diseaseRecommendation'
          : recommendation?.seedTreatmentRecommendation
            ? 'seedTreatmentRecommendation'
            : 'plagueRecommendation';

    const applicationOptions = useMemo(() => getApplicationOptions(), []);
    const currentApplication =
        recommendationKey === 'seedTreatmentRecommendation'
            ? undefined
            : applicationOptions[recommendation?.[recommendationKey]?.result?.applicationMoment];

    const applicationTypeOptions = useMemo(() => getApplicationTypeOptions(), []);
    const currentApplicationType =
        applicationTypeOptions[recommendation?.diseaseRecommendation?.result?.applicationType];

    const renderMonth = (month: string, disabled = false) => (
        <Box
            sx={{
                background: disabled ? '#F5F5F5' : pdfTheme.mui.secondary.colors.base,
                color: disabled ? 'gray !important' : 'white !important',
                padding: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <Typography variant='subtitle2'>{month}</Typography>
        </Box>
    );

    const renderProduct = (name: string, dose: string, logoSrc: string, index: number) => (
        <Stack
            sx={{
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                background: '#E7F8FF',
                borderRadius: 2,
                py: 2,
                px: 1,
                minWidth: 120,
                minHeight: 150,
            }}
        >
            {logoSrc && (
                <Box
                    component='img'
                    src={logoSrc}
                    alt={name}
                    sx={{
                        width: 50,
                        height: 50,
                    }}
                />
            )}
            <Typography variant='caption' fontWeight={'bold'}>
                {name}
            </Typography>
            <Typography variant='caption'>{dose}</Typography>
        </Stack>
    );

    const parsedOptions: Option[] = useMemo(() => {
        const options = recommendation?.[recommendationKey]?.result?.options;
        return (
            options.map((option) => ({
                name: _(option.name),
                comments: _(option.comments),
                type: _(option.type),
                columns: option.columns.map((column) => ({
                    name: _(column.name),
                    condition: _(column.condition),
                    products: column.products.map((product) => ({
                        dose: _(product.dose),
                        name: _(product.product.name),
                        image: product.product.image,
                    })),
                })),
            })) || []
        );
    }, [recommendation]);

    const getMonthsForOption = (option) => {
        if (!recommendation || !option) return [];
        if (recommendationKey === 'seedTreatmentRecommendation') {
            return getSeedTreatmentMonths(option, recommendation?.seedTreatmentRecommendation?.result?.crop);
        }
        if (!currentApplication && !currentApplicationType) return [];
        if (recommendationKey === 'weedV2' || recommendationKey === 'plagueRecommendation') {
            const reco = recommendation?.[recommendationKey];
            return (
                currentApplication?.getMonths({
                    plantingDate: reco.plantingDate,
                    createdDate: recommendation.created,
                    option: option, // TODO: TAB
                    applicationWindow: reco.result?.applicationWindow,
                    crop: reco.result?.crop,
                }) || []
            );
        }
        if (recommendation?.[recommendationKey]?.result.crop === 'soy') {
            return [
                {
                    disabled: true,
                },
                {
                    disabled: false,
                    columns: option.columns,
                    icons: ['r3', 'r4', 'r5'],
                },
                {
                    disabled: true,
                },
            ];
        }
        if (recommendation?.[recommendationKey]?.result.crop === 'corn') {
            return [
                {
                    disabled: true,
                },
                {
                    disabled: false,
                    columns: option.columns,
                    icons: ['v8', 'v9', 'v10', 'r1'],
                },
                {
                    disabled: true,
                },
            ];
        }
        if (['wheat', 'barley'].includes(recommendation?.[recommendationKey]?.result.crop)) {
            return getCerealDiseaseMonths(option);
        }
        return [];
    };

    const renderOptionContent = (option, months) => {
        const applicationTypeIndexes = months.reduce(
            (acc, month, index) => {
                if (!month.disabled) {
                    if (acc[0] === undefined) acc[0] = index;
                    acc[1] = index;
                }
                return acc;
            },
            [undefined, undefined]
        );

        const generatePlagueRecommendationText = (applicationWindow?: string) => {
            if (applicationWindow === 'v2_to_v10') {
                return _('Desde estadío v2 hasta v10');
            }
            if (applicationWindow === 'v1_to_v8') {
                return _('Desde estadío v1 hasta v8');
            }
            return false;
        };

        return (
            <Stack spacing={2} key={option.name}>
                {option.name && <Typography variant='h6'>{option.name}</Typography>}
                <Stack
                    direction='row'
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 1, sm: `repeat(${months.length}, 1fr)` },
                        width: '100%',
                    }}
                >
                    {months.map((month, i) => (
                        <Stack
                            key={i}
                            sx={{
                                background: month.disabled ? '#F5F5F5' : '',
                                flex: 1,
                                borderRight: '1px dashed lightgray',
                                display: {
                                    xs:
                                        (month.columns?.length || 0) > 0 || (month.icons?.length || 0) > 0
                                            ? 'flex'
                                            : 'none',
                                    sm: 'flex',
                                },
                            }}
                        >
                            {month.name && renderMonth(month.name, month.disabled)}
                            {month.columns && (
                                <Stack direction='row' spacing={2} p={2} alignSelf={'center'}>
                                    {month.columns.map((column) => (
                                        <Stack direction='row' height={'fit-content'} key={column.name}>
                                            <Stack p={1} justifyContent='center' alignItems='center'>
                                                <RadioButtonChecked sx={{ color: pdfTheme.mui.secondary.secondary }} />
                                                <Divider
                                                    orientation='vertical'
                                                    sx={{
                                                        borderStyle: 'dashed',
                                                        borderColor: pdfTheme.mui.secondary.secondary,
                                                    }}
                                                />
                                            </Stack>
                                            <Stack flex={1} spacing={1}>
                                                <Stack>
                                                    <Typography
                                                        variant='caption'
                                                        sx={{ height: 20, whiteSpace: 'nowrap' }}
                                                    >
                                                        {column.name}
                                                    </Typography>

                                                    <Typography
                                                        variant='caption'
                                                        fontWeight='bold'
                                                        sx={{
                                                            height: 20,
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {column.condition}
                                                    </Typography>
                                                </Stack>

                                                <Stack spacing={1} mt={1} alignItems='center'>
                                                    {column.products.map((product, index) => (
                                                        <Fragment key={index}>
                                                            {renderProduct(
                                                                product.name,
                                                                product.dose,
                                                                product.image,
                                                                index
                                                            )}
                                                            {index + 1 < column.products.length && (
                                                                <Add sx={{ color: pdfTheme.mui.primary.primary }} />
                                                            )}
                                                        </Fragment>
                                                    ))}
                                                </Stack>
                                            </Stack>
                                        </Stack>
                                    ))}
                                </Stack>
                            )}
                            {month.icons && (
                                <Box display='flex' justifyContent='space-evenly' alignItems='center' mt={'auto'}>
                                    {month.icons.map((icon) => (
                                        <Box
                                            key={icon}
                                            sx={{
                                                maxWidth: 50,
                                                width: 50,
                                                marginBottom: getPhenologyIconMarginBottom(icon, month.disabled),
                                                '& svg': { width: '100%', height: 'auto' },
                                            }}
                                            display='flex'
                                            justifyContent='center'
                                            dangerouslySetInnerHTML={{
                                                __html: PhenologiesIcons[icon],
                                            }}
                                        />
                                    ))}
                                </Box>
                            )}
                        </Stack>
                    ))}
                    {months?.length > 0 &&
                        // only render the application-window banner when there is an enabled
                        // month; otherwise the grid-column math below resolves to NaN
                        applicationTypeIndexes[0] !== undefined &&
                        !(
                            // do not show only for plague recos without application window
                            (
                                recommendation?.plagueRecommendation &&
                                recommendation?.plagueRecommendation?.result?.applicationWindow === ''
                            )
                        ) && (
                            <Typography
                                variant='caption'
                                sx={{
                                    gridColumnStart: {
                                        xs: 0,
                                        sm: applicationTypeIndexes[0] + 1,
                                    },
                                    gridColumnEnd: {
                                        xs: 1,
                                        sm: applicationTypeIndexes[1] + 2,
                                    },
                                    background: '#006F9B',
                                    color: 'white !important',
                                    p: 1,
                                    textAlign: 'center',
                                }}
                            >
                                {recommendation?.weedV2
                                    ? `${currentApplication?.title({
                                          crop: recommendation?.weedV2?.result?.crop,
                                      })} (${currentApplication?.description({
                                          type: option?.type,
                                      })})`
                                    : recommendation?.plagueRecommendation
                                      ? generatePlagueRecommendationText(
                                            recommendation?.plagueRecommendation.result?.applicationWindow
                                        )
                                      : recommendation?.seedTreatmentRecommendation
                                        ? _('Siembra')
                                        : recommendation?.diseaseRecommendation &&
                                            recommendation?.diseaseRecommendation.result?.crop === 'soy'
                                          ? _('Estadío R3 a R5')
                                          : ['wheat', 'barley'].includes(
                                                  recommendation?.diseaseRecommendation?.result?.crop
                                              )
                                            ? _('De Encañazón a Espigazón')
                                            : _('Estadío V8 a R1')}
                            </Typography>
                        )}
                </Stack>
                {option.comments && (
                    <Stack>
                        <Typography
                            variant='subtitle1'
                            color={pdfTheme.mui.primary.primary}
                            sx={{
                                fontWeight: 700,
                            }}
                        >
                            {_('A tener en cuenta')}
                        </Typography>
                        <Typography color={pdfTheme.mui.primary.primary} variant='body2'>
                            {option.comments}
                        </Typography>
                    </Stack>
                )}
            </Stack>
        );
    };

    return (
        <Stack spacing={4}>
            {parsedOptions.map((option, index) => (
                <Stack spacing={2} key={option.name}>
                    {parsedOptions.length > 1 && (
                        <Typography color='primary' variant='body1' sx={{ fontWeight: 600 }}>
                            {_('OPCIÓN')} {index + 1}
                        </Typography>
                    )}
                    {renderOptionContent(option, getMonthsForOption(option))}
                </Stack>
            ))}
        </Stack>
    );
};
